import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  InputAdornment,
  IconButton,
  Collapse,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { Card, Pill, SectionHeader } from '../components/shared';
import { SAGE, CREAM, WARM } from '../theme';
import { fetchApplications, createApplication } from '../services/applicationService';
import { API_URL } from '../config';
import { mockAuthHeaders } from '../utils/mockAuth';

// Tab definitions
const TABS = [
  { key: 'in_progress', label: 'In Progress', dotColor: SAGE[500] },
  { key: 'submitted', label: 'Submitted', dotColor: WARM.accent },
  { key: 'inactive', label: 'Inactive', dotColor: WARM.rose },
];

const TAB_BANNERS = {
  in_progress: 'Match quality and workflow progress update live as AI processes your applications.',
  submitted: 'Track your submitted applications and interview progress.',
  inactive: 'Applications that have been closed or denied.',
};

// Filter functions
const tabFilters = {
  in_progress: (app) => app.status === 'Unsubmitted',
  submitted: (app) => app.status === 'Submitted' || (app.status && app.status.includes('Interview')),
  inactive: (app) => app.status === 'Denied',
};

const ApplicationsPage = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('in_progress');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [sortConfig, setSortConfig] = useState({ field: null, direction: 'asc' });
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);

  // Load applications
  const loadApplications = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchApplications();
      setApplications(data);
    } catch (error) {
      console.error('[APPS] Error loading applications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadApplications(); }, [loadApplications]);

  // Visibility refresh
  useEffect(() => {
    const handler = () => { if (document.visibilityState === 'visible') loadApplications(); };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [loadApplications]);

  // Smart polling
  useEffect(() => {
    const hasActiveImports = applications.some(
      app => app.import_status === 'in_progress' || app.import_status === 'pending'
    );
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') loadApplications();
    }, hasActiveImports ? 3000 : 30000);
    return () => clearInterval(interval);
  }, [applications, loadApplications]);

  // Import from URL
  const handleImportFromUrl = async () => {
    if (!importUrl.trim()) return;
    setImporting(true);
    try {
      const response = await fetch(`${API_URL}/api/automation/import-from-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...mockAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ url: importUrl }),
      });
      if (response.ok) {
        setImportDialogOpen(false);
        setImportUrl('');
        await loadApplications();
      } else {
        alert('Failed to import. Please try again.');
      }
    } catch (error) {
      console.error('[APPS] Import error:', error);
      alert('Error importing application.');
    } finally {
      setImporting(false);
    }
  };

  // Tab counts
  const tabCounts = useMemo(() => ({
    in_progress: applications.filter(tabFilters.in_progress).length,
    submitted: applications.filter(tabFilters.submitted).length,
    inactive: applications.filter(tabFilters.inactive).length,
  }), [applications]);

  // Filter + search + sort
  const filteredApps = useMemo(() => {
    let apps = applications.filter(tabFilters[activeTab]);

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      apps = apps.filter(a =>
        (a.company || '').toLowerCase().includes(q) ||
        (a.position || '').toLowerCase().includes(q)
      );
    }

    if (sortConfig.field) {
      apps = [...apps].sort((a, b) => {
        let aVal = a[sortConfig.field];
        let bVal = b[sortConfig.field];

        // Handle dates
        if (sortConfig.field === 'created_at' || sortConfig.field === 'application_date') {
          aVal = aVal ? new Date(aVal).getTime() : 0;
          bVal = bVal ? new Date(bVal).getTime() : 0;
        }

        // Handle numbers
        if (typeof aVal === 'number' || typeof bVal === 'number') {
          aVal = aVal || 0;
          bVal = bVal || 0;
        }

        // Handle strings
        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return apps;
  }, [applications, activeTab, searchQuery, sortConfig]);

  const toggleExpand = (id, e) => {
    e.stopPropagation();
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  // Column definitions per tab
  const columnDefs = {
    in_progress: [
      { field: 'company', label: 'Company', flex: 1.2 },
      { field: 'position', label: 'Position', flex: 1.5 },
      { field: 'status', label: 'Status', flex: 0.8, render: (app) => <StatusPill app={app} /> },
      { field: 'match_score', label: 'Match %', flex: 0.6, render: (app) => <MatchDisplay score={app.match_score} /> },
      { field: 'workflow_stage', label: 'Stage', flex: 0.7, render: (app) => <StagePill stage={app.workflow_stage} /> },
      { field: 'created_at', label: 'Last Updated', flex: 0.8, render: (app) => <DateDisplay date={app.created_at} milestones={app.workflow_milestones} /> },
    ],
    submitted: [
      { field: 'company', label: 'Company', flex: 1.2 },
      { field: 'position', label: 'Position', flex: 1.5 },
      { field: 'status', label: 'Stage', flex: 0.8, render: (app) => <StatusPill app={app} /> },
      { field: 'match_score', label: 'Match at Submit', flex: 0.7, render: (app) => <MatchDisplay score={app.match_score} /> },
      { field: 'application_date', label: 'Submitted', flex: 0.8, render: (app) => formatDate(app.application_date) },
      { field: 'created_at', label: 'Last Activity', flex: 0.8, render: (app) => <DateDisplay date={app.created_at} milestones={app.workflow_milestones} /> },
    ],
    inactive: [
      { field: 'company', label: 'Company', flex: 1.2 },
      { field: 'position', label: 'Position', flex: 1.5 },
      { field: 'status', label: 'Outcome', flex: 0.8, render: (app) => <Pill preset="rose" label={app.status} /> },
      { field: 'match_score', label: 'Match', flex: 0.6, render: (app) => <MatchDisplay score={app.match_score} /> },
      { field: 'created_at', label: 'Closed Date', flex: 0.8, render: (app) => formatDate(app.created_at) },
    ],
  };

  const columns = columnDefs[activeTab];

  // Header cell styles
  const headerSx = {
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 600,
    fontSize: '0.75rem',
    color: SAGE[500],
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    cursor: 'pointer',
    userSelect: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    '&:hover': { color: SAGE[700] },
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
          <Typography
            variant="h4"
            sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: SAGE[900] }}
          >
            Applications
          </Typography>
          <Typography sx={{ fontFamily: "'Outfit', sans-serif", color: SAGE[400], fontSize: '1rem' }}>
            {applications.length}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setImportDialogOpen(true)}
          sx={{
            bgcolor: SAGE[500],
            fontFamily: "'Outfit', sans-serif",
            fontWeight: 500,
            textTransform: 'none',
            borderRadius: 2,
            '&:hover': { bgcolor: SAGE[600] },
          }}
        >
          New Application
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        {TABS.map(({ key, label, dotColor }) => {
          const active = activeTab === key;
          return (
            <Box
              key={key}
              onClick={() => setActiveTab(key)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 3,
                cursor: 'pointer',
                bgcolor: active ? SAGE[100] : 'transparent',
                border: `1.5px solid ${active ? SAGE[300] : 'transparent'}`,
                transition: 'all 0.15s ease',
                '&:hover': { bgcolor: SAGE[50] },
              }}
            >
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor }} />
              <Typography
                sx={{
                  fontFamily: "'Outfit', sans-serif",
                  fontWeight: active ? 600 : 400,
                  fontSize: '0.875rem',
                  color: active ? SAGE[800] : SAGE[500],
                }}
              >
                {label}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Outfit', sans-serif",
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  color: active ? SAGE[600] : SAGE[400],
                }}
              >
                {tabCounts[key]}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search by company or position..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: SAGE[300], fontSize: 20 }} />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: CREAM[50],
            fontFamily: "'Lora', serif",
            fontSize: '0.875rem',
            '& fieldset': { borderColor: SAGE[200] },
            '&:hover fieldset': { borderColor: SAGE[300] },
          },
        }}
      />

      {/* Context Banner */}
      <Box
        sx={{
          px: 2,
          py: 1,
          mb: 2,
          borderRadius: 2,
          bgcolor: SAGE[50],
          border: `1px solid ${SAGE[100]}`,
        }}
      >
        <Typography variant="body2" sx={{ color: SAGE[600], fontSize: '0.8rem' }}>
          {TAB_BANNERS[activeTab]}
        </Typography>
      </Box>

      {/* Table */}
      <Card sx={{ overflow: 'hidden' }}>
        {/* Header Row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${SAGE[100]}`,
            bgcolor: CREAM[100],
          }}
        >
          <Box sx={{ width: 36 }} /> {/* expand icon space */}
          {columns.map(col => (
            <Box key={col.field} sx={{ flex: col.flex, ...headerSx }} onClick={() => handleSort(col.field)}>
              {col.label}
              {sortConfig.field === col.field && (
                sortConfig.direction === 'asc'
                  ? <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                  : <ArrowDownwardIcon sx={{ fontSize: 14 }} />
              )}
            </Box>
          ))}
        </Box>

        {/* Loading */}
        {loading && applications.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} sx={{ color: SAGE[400] }} />
          </Box>
        )}

        {/* Empty State */}
        {!loading && filteredApps.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography sx={{ fontFamily: "'Outfit', sans-serif", color: SAGE[400], fontSize: '0.9rem' }}>
              {searchQuery ? 'No matching applications found' : 'No applications in this category'}
            </Typography>
          </Box>
        )}

        {/* Rows */}
        {filteredApps.map(app => {
          const isExpanded = expandedRows[app.id];
          const isImporting = app.import_status === 'in_progress' || app.import_status === 'pending';

          return (
            <React.Fragment key={app.id}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 2,
                  py: 1.5,
                  borderBottom: `1px solid ${SAGE[50]}`,
                  cursor: 'pointer',
                  transition: 'background 0.1s ease',
                  '&:hover': { bgcolor: SAGE[50] },
                  ...(isExpanded && { bgcolor: CREAM[200] }),
                }}
                onClick={() => navigate(`/applications/${app.id}`)}
              >
                {/* Expand toggle */}
                <IconButton
                  size="small"
                  onClick={(e) => toggleExpand(app.id, e)}
                  sx={{ width: 36, color: SAGE[400] }}
                >
                  {isExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                </IconButton>

                {/* Columns */}
                {columns.map(col => (
                  <Box
                    key={col.field}
                    sx={{
                      flex: col.flex,
                      display: 'flex',
                      alignItems: 'center',
                      minWidth: 0,
                    }}
                  >
                    {col.render ? col.render(app) : (
                      <Typography
                        sx={{
                          fontFamily: col.field === 'company' || col.field === 'position' ? "'Outfit', sans-serif" : "'Lora', serif",
                          fontSize: '0.85rem',
                          color: SAGE[800],
                          fontWeight: col.field === 'company' ? 600 : 400,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {isImporting && col.field === 'position' ? 'Importing...' : (app[col.field] || '—')}
                      </Typography>
                    )}
                    {isImporting && col.field === 'company' && (
                      <CircularProgress size={14} sx={{ ml: 1, color: SAGE[400] }} />
                    )}
                  </Box>
                ))}
              </Box>

              {/* Expanded Detail */}
              <Collapse in={isExpanded} timeout={150}>
                <ExpandedRow app={app} navigate={navigate} />
              </Collapse>
            </React.Fragment>
          );
        })}
      </Card>

      {/* Import Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={() => !importing && setImportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesomeIcon sx={{ color: WARM.accent }} />
            <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
              Import Application
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: SAGE[600], mb: 2 }}>
            Paste a job posting URL and AI will extract all the details.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Job Posting URL"
            value={importUrl}
            onChange={(e) => setImportUrl(e.target.value)}
            placeholder="https://company.com/careers/job-posting"
            disabled={importing}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !importing) handleImportFromUrl();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)} disabled={importing}>Cancel</Button>
          <Button
            onClick={handleImportFromUrl}
            variant="contained"
            disabled={importing || !importUrl.trim()}
            startIcon={importing ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
            sx={{ bgcolor: SAGE[500], '&:hover': { bgcolor: SAGE[600] } }}
          >
            {importing ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ── Helper Components ──

const StatusPill = ({ app }) => {
  const isImporting = app.import_status === 'in_progress' || app.import_status === 'pending';
  if (isImporting) return <Pill preset="warm" label="Importing" />;

  const presetMap = {
    'Unsubmitted': 'sage',
    'Submitted': 'warm',
    'Denied': 'rose',
  };
  const preset = presetMap[app.status] || 'neutral';
  return <Pill preset={preset} label={app.status} />;
};

const MatchDisplay = ({ score }) => {
  if (!score) return <Typography sx={{ color: SAGE[300], fontSize: '0.8rem' }}>—</Typography>;
  const preset = score >= 80 ? 'sage' : score >= 60 ? 'warm' : 'rose';
  return <Pill preset={preset} label={`${Math.round(score)}%`} />;
};

const StagePill = ({ stage }) => {
  const labels = { 1: 'JD', 2: 'Resume', 3: 'Cover Letter', 4: 'Ready' };
  const s = stage || 1;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {[1, 2, 3, 4].map(n => (
        <Box
          key={n}
          sx={{
            width: 16,
            height: 4,
            borderRadius: 1,
            bgcolor: n <= s ? SAGE[500] : SAGE[200],
          }}
        />
      ))}
      <Typography sx={{ fontSize: '0.7rem', color: SAGE[500], ml: 0.5, fontFamily: "'Outfit', sans-serif" }}>
        {labels[s] || `S${s}`}
      </Typography>
    </Box>
  );
};

const DateDisplay = ({ date, milestones }) => {
  // Use the most recent milestone or created_at
  let displayDate = date;
  if (milestones) {
    const timestamps = Object.values(milestones).filter(Boolean).map(d => new Date(d).getTime());
    if (timestamps.length > 0) {
      displayDate = new Date(Math.max(...timestamps)).toISOString();
    }
  }

  const formatRelative = (dateStr) => {
    if (!dateStr) return '—';
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <Typography sx={{ fontSize: '0.8rem', color: SAGE[600], fontFamily: "'Lora', serif" }}>
      {formatRelative(displayDate)}
    </Typography>
  );
};

const formatDate = (dateStr) => {
  if (!dateStr) return <Typography sx={{ fontSize: '0.8rem', color: SAGE[300] }}>—</Typography>;
  return (
    <Typography sx={{ fontSize: '0.8rem', color: SAGE[600], fontFamily: "'Lora', serif" }}>
      {new Date(dateStr).toLocaleDateString()}
    </Typography>
  );
};

const ExpandedRow = ({ app, navigate }) => {
  const stageLabels = { 1: 'Job Description', 2: 'Resume', 3: 'Cover Letter', 4: 'Ready to Submit' };
  const milestones = app.workflow_milestones || {};

  const milestoneList = [
    { key: 'jd_selected_at', label: 'JD Selected' },
    { key: 'jd_structured_at', label: 'JD Structured' },
    { key: 'criteria_extracted_at', label: 'Criteria Extracted' },
    { key: 'resume_selected_at', label: 'Resume Selected' },
    { key: 'resume_matched_at', label: 'Resume Matched' },
    { key: 'recommendations_generated_at', label: 'Recommendations' },
    { key: 'resume_finalized_at', label: 'Resume Finalized' },
    { key: 'cover_letter_drafted_at', label: 'Cover Letter Drafted' },
    { key: 'cover_letter_finalized_at', label: 'Cover Letter Finalized' },
  ].filter(m => milestones[m.key]);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: 3,
        px: 4,
        py: 3,
        bgcolor: CREAM[200],
        borderBottom: `1px solid ${SAGE[100]}`,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Details */}
      <Box>
        <SectionHeader sx={{ mb: 1 }}>Details</SectionHeader>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <DetailRow label="Stage" value={stageLabels[app.workflow_stage] || 'Getting Started'} />
          {(app.salary_min || app.salary_max) && (
            <DetailRow
              label="Salary"
              value={`$${app.salary_min ? Math.round(app.salary_min / 1000) + 'k' : '?'} – $${app.salary_max ? Math.round(app.salary_max / 1000) + 'k' : '?'}`}
            />
          )}
          {app.application_url && (
            <DetailRow label="Source" value={
              <a
                href={app.application_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: SAGE[600], fontSize: '0.8rem' }}
                onClick={(e) => e.stopPropagation()}
              >
                View Posting
              </a>
            } />
          )}
          <DetailRow label="Created" value={new Date(app.created_at).toLocaleDateString()} />
        </Box>
      </Box>

      {/* Milestones */}
      <Box>
        <SectionHeader sx={{ mb: 1 }}>Progress</SectionHeader>
        {milestoneList.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {milestoneList.map(m => (
              <Box key={m.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '0.8rem', color: SAGE[700] }}>{m.label}</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: SAGE[400] }}>
                  {new Date(milestones[m.key]).toLocaleDateString()}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography sx={{ fontSize: '0.8rem', color: SAGE[400] }}>No milestones yet</Typography>
        )}
      </Box>

      {/* Actions */}
      <Box>
        <SectionHeader sx={{ mb: 1 }}>Actions</SectionHeader>
        {app.match_score && (
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: '0.75rem', color: SAGE[500], mb: 0.5 }}>Match Score</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: SAGE[100] }}>
                <Box
                  sx={{
                    width: `${Math.min(app.match_score, 100)}%`,
                    height: '100%',
                    borderRadius: 3,
                    bgcolor: app.match_score >= 80 ? SAGE[500] : app.match_score >= 60 ? WARM.accent : WARM.rose,
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: SAGE[700] }}>
                {Math.round(app.match_score)}%
              </Typography>
            </Box>
          </Box>
        )}
        <Button
          variant="outlined"
          size="small"
          endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
          onClick={() => navigate(`/applications/${app.id}`)}
          sx={{
            textTransform: 'none',
            fontFamily: "'Outfit', sans-serif",
            borderColor: SAGE[300],
            color: SAGE[700],
            borderRadius: 2,
            '&:hover': { borderColor: SAGE[500], bgcolor: SAGE[50] },
          }}
        >
          Open Application
        </Button>
      </Box>
    </Box>
  );
};

const DetailRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <Typography sx={{ fontSize: '0.75rem', color: SAGE[500] }}>{label}</Typography>
    {typeof value === 'string' ? (
      <Typography sx={{ fontSize: '0.8rem', color: SAGE[700], fontWeight: 500 }}>{value}</Typography>
    ) : value}
  </Box>
);

export default ApplicationsPage;
